import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { FormControl, InputAdornment, TextField as MuiTextField } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';

type NumberInputProps = Pick<MuiTextFieldProps, 'autoFocus' | 'id' | 'required'> & {
  endAdornment?: string;
  errorText?: string;
  helperText?: string;
  inputWidth?: '10rem' | '15rem' | '20rem' | '100%';
  label: string;
  onChange: (value: number | undefined) => void;
  overrideVisited?: boolean;
  startAdornment?: string;
  value?: number;
};

const validInput = /^-?(?:\d+(?:[.,]\d*)?|[.,]\d*)?$/;

function formatValue(value: number | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toString() : '';
}

export function NumberInput({
  autoFocus = false,
  endAdornment,
  errorText = '',
  helperText = '',
  id,
  inputWidth = '100%',
  label,
  onChange,
  overrideVisited = false,
  required = false,
  startAdornment,
  value,
}: NumberInputProps) {
  const generatedId = useId();

  const [visited, setVisited] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState(() => formatValue(value));
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setInputValue(formatValue(value));
    }
  }, [value]);

  const textFieldId = id ?? generatedId;
  const labelId = `${textFieldId}-label`;
  const helperTextId = `${textFieldId}-helper-text`;
  const maxWidth = inputWidth === '100%' ? inputWidth : '20rem';

  function handleChange(nextValue: string) {
    if (!validInput.test(nextValue)) return;

    setInputValue(nextValue);
    setVisited(true);

    if (nextValue === '') {
      onChange(undefined);
      return;
    }

    const parsedValue = Number(nextValue.replace(',', '.'));
    if (Number.isFinite(parsedValue)) {
      onChange(parsedValue);
    }
  }

  const showError = !!errorText && (visited || overrideVisited);

  return (
    <FormControl fullWidth sx={{ maxWidth }}>
      <FormLabel
        componentId={textFieldId}
        id={labelId}
        label={label}
        required={required}
        valid={errorText.length === 0}
      />

      <MuiTextField
        autoFocus={autoFocus}
        error={!!errorText && (visited || overrideVisited)}
        id={textFieldId}
        name={textFieldId}
        slotProps={{
          input: {
            startAdornment: startAdornment ? (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ) : undefined,
            endAdornment: endAdornment ? (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ) : undefined,
          },
          htmlInput: {
            'aria-describedby': helperTextId,
            inputMode: 'decimal',
          },
        }}
        onBlur={() => {
          isFocused.current = false;
          setInputValue(formatValue(value));
          setVisited(true);
        }}
        onChange={({ target: { value: nextValue } }) => handleChange(nextValue)}
        onFocus={() => {
          isFocused.current = true;
        }}
        required={required}
        sx={{
          width: inputWidth,
          maxWidth: '100%',
          '& .MuiInputBase-input': {
            padding: '8px 12px',
          },
        }}
        type="text"
        value={inputValue}
        variant="outlined"
      />

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? errorText : helperText}
      />
    </FormControl>
  );
}
